import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CallEvent } from 'matrix-js-sdk';
import { CallState } from 'matrix-js-sdk/lib/webrtc/call';
import { CallFeedEvent } from 'matrix-js-sdk/lib/webrtc/callFeed';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import { MatrixCallContext } from '../../../../MatrixCallProvider';
import { MatrixContext } from '../../../../MatrixProvider';
import { hasInProgressMatrixCall } from '../../../../shared/components/matrix/matrixUtils';
import { ROLE } from '../../../../shared/constants/common';
import {
  getUserRole,
} from '../../../../shared/utils/function';
import { useMatrixRoomInfo } from '../../../../shared/utils/matrix/useMatrixRoomInfo';
import { fetchWalletBalanceAction } from '../../../../shared/actions/creditAction';
import { setCallFlowActive } from '../../../../shared/utils/config/paymentFlowGuard';
import { useHandleTipSuccessInChat } from '../Chating/hooks/useHandleTipSuccessInChat';
import { useSendTextMessageFromCall } from './hooks/useSendTextMessageFromCall';
import VideoCallUI from './VideoCallUI';
import VoiceCallUI from './VoiceCallUI';
import CallErrorState from './component/CallErrorState';
import CallRemoteAudio from './component/CallRemoteAudio';
import CallScreenCenteredMessage from './component/CallScreenCenteredMessage';
import { openBrowserMediaSettings } from './utils/openBrowserMediaSettings';
import CallingScreenSubscriberModals from './component/CallingScreenSubscriberModals';
import { acquireCallMediaCapture } from '../../../../shared/services/call/callMediaCaptureService';
import {
  clearOutgoingPlaceCallBlock,
  consumePendingCallMediaError,
  shouldBlockOutgoingPlaceCall,
} from '../../../../shared/services/call/callMediaAccessGate';
import {
  areFeedsEquivalent,
  feedHasAudio,
  feedHasTracks,
  feedHasVideo,
  getRemoteUsermediaFeed,
  getSingleRemoteAudioTrackStream,
  isCallFeedVideoMuted,
  pickPreferredRemoteStream,
  streamHasLiveVideoTrack,
} from './utils/callFeedUtils';
import { useCallLifecycleNotices } from './hooks/useCallLifecycleNotices';
import { useCallSummaryRedirect } from './hooks/useCallSummaryRedirect';
import { useCallTipEarnings } from './hooks/useCallTipEarnings';
import { useLowBalanceCreditCallNotify } from './hooks/useLowBalanceCreditCallNotify';
import { useSubscriberCallCreditDeduction } from './hooks/useSubscriberCallCreditDeduction';
import { useOpponentCallProfile } from './hooks/useOpponentCallProfile';
import { useCallRouteParams } from './hooks/useCallRouteParams';
import { useCallingScreenMatrixRoom } from './hooks/useCallingScreenMatrixRoom';
import { useCreatorOutgoingSubscriberCredits } from './hooks/useCreatorOutgoingSubscriberCredits';
import { useCallRouteEntryGuard } from './hooks/useCallRouteEntryGuard';
import {
  setLocalOutboundAudioTracksEnabled,
  toggleCallMicrophone,
} from './utils/callControlUtils';
import { useSubscriberCallCameraToggle } from './hooks/useSubscriberCallCameraToggle';
import {
  calculateCreatorEarningsUsdPerSecond,
  formatCallDuration,
  formatCreditsValue,
  getCallRequestPricingDetails,
  getCreatorRatePerMinuteUsd,
} from './utils/callFormatUtils';
import { useCallDurationTimer } from './utils/callTimelineUtils';
import { useCallCameraFlip } from './hooks/useCallCameraFlip';
import {
  forceDetachMediaElements as detachCallMediaElementsFromRefs,
  forceStopStreamTracks,
} from './utils/callMediaAttachUtils';
import { sendJuicyhouseCallEvent } from '../../../../shared/utils/matrix/matrixReadUtils';
import { buildCallRateOfferEventContent, mergeCallRateIntoCreatorInfo } from './utils/callRateOfferUtils';
import { logCallError } from './utils/callLogError';
import { FEEDS_DEBOUNCE_MS } from './constant';
import { useCallSession } from './hooks/useCallSession';
import { useCallMediaAttachment } from './hooks/useCallMediaAttachment';
import { useCreatorOutgoingCallPricing } from './hooks/useCreatorOutgoingCallPricing';
import { useCallNetworkResilience } from './hooks/useCallNetworkResilience';

const CallingScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const currentUser = useSelector((state) => state.user?.user);
  const walletBalance = useSelector((state) => state.credit?.walletBalance);
  const walletBalanceLoading = useSelector((state) => state.credit?.walletBalanceLoading);
  const {
    roomId,
    callType,
    callUiType,
    isVoiceCallUi,
    callMediaType,
    shouldAutoPlaceCall,
    isIncomingCallRoute,
    callRateOverride,
  } = useCallRouteParams();
  const { client, isInitialized } = useContext(MatrixContext);
  const {
    activeCall,
    incomingCall,
    callError,
    lastCallEndedReason,
    lastCallHangupParty,
    placeVideoCall,
    placeVoiceCall,
    answerCall,
    endCall,
    clearError,
    reportMediaError,
    clearLastCallEndState,
    resyncCallsFromSdk,
  } = useContext(MatrixCallContext);

  const [localFeeds, setLocalFeeds] = useState([]);
  const [remoteFeeds, setRemoteFeeds] = useState([]);
  const [isRemoteCameraOff, setIsRemoteCameraOff] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const [sdkCallState, setSdkCallState] = useState(null);
  const [showPurchaseCreditsModal, setShowPurchaseCreditsModal] = useState(false);
  /** Matches SDK connected state; used for duration anchor notice and pause/resume. */
  const isCallConnected =
    sdkCallState === CallState.Connected || sdkCallState === 'connected';
  /** When true, PurchaseCreditsModal shows the in-call low-balance layout (subscriber only). */
  const [purchaseCreditsLowCreditMode, setPurchaseCreditsLowCreditMode] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipText, setTipText] = useState('');
  const [callTipLedgerRevision, setCallTipLedgerRevision] = useState(0);
  const bumpCallTipLedger = useCallback(() => {
    setCallTipLedgerRevision((n) => n + 1);
  }, []);

  const { session, dispatch: dispatchCallSession, hasConnectedRef, hadLiveCallRef } = useCallSession();

  const openTipModalRefForCall = useRef({});
  const forceScrollToBottomRefForCall = useRef(false);

  const currentRole = getUserRole();
  const isCreator = currentRole === ROLE.CREATOR_SUB || currentRole === ROLE.CREATOR_CONTENT;
  const isSubscriber = currentRole === ROLE.USER;
  const skipLowBalanceProcessForTesting =
    String(process.env.REACT_APP_SKIP_LOW_BALANCE_PROCESS_FOR_TESTING).toLowerCase() === 'true';

  const { roomMetadata, resolvedUserId, targetSubscriberId, matrixRoom, matrixLoading, hasRoomsLoaded } = useCallingScreenMatrixRoom({
    client,
    roomId,
  });

  useCallRouteEntryGuard({
    navigate,
    roomId,
    shouldAutoPlaceCall,
    hasRoomsLoaded,
    matrixLoading,
    roomMetadata,
    targetSubscriberId,
    activeCall,
  });

  useEffect(() => {
    if (!isIncomingCallRoute || activeCall || callError) return undefined;

    if (incomingCall) {
      navigate.toMessages();
      return undefined;
    }

    resyncCallsFromSdk?.();

    const timeoutId = window.setTimeout(() => {
      if (resolvedUserId) {
        navigate.toChat(resolvedUserId, {
          replace: true,
          state: roomId ? { preferredRoomId: roomId } : undefined,
        });
      } else {
        navigate.toMessages();
      }
    }, 12000);

    return () => window.clearTimeout(timeoutId);
  }, [
    isIncomingCallRoute,
    activeCall,
    callError,
    incomingCall,
    resyncCallsFromSdk,
    navigate,
    resolvedUserId,
    roomId,
  ]);

  useEffect(() => {
    if (isCreator) return;
    const subscriberId = currentUser?._id || currentUser?.id;
    if (!subscriberId) return;
    dispatch(fetchWalletBalanceAction(subscriberId));
  }, [dispatch, isCreator, currentUser?._id, currentUser?.id]);

  const { destUserInfo, loading: callRoomInfoLoading } = useMatrixRoomInfo(resolvedUserId);
  const creatorInfoForBillingBase = destUserInfo || roomMetadata?.otherUser || null;

  useEffect(() => {
    setCallFlowActive(true);
    return () => setCallFlowActive(false);
  }, []);

  // Gesture-time media failure from Start Call → show Allow access before placeCall.
  useEffect(() => {
    const pending = consumePendingCallMediaError();
    if (pending) reportMediaError?.(pending);
  }, [reportMediaError]);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const micMutedRef = useRef(false);
  const requestedMicMutedRef = useRef(false);
  const appliedMicMutedRef = useRef(false);
  const micToggleInFlightRef = useRef(false);
  const listenersRef = useRef(null);
  const hasRedirectedRef = useRef(false);
  const missedCallNoticeSentRef = useRef(false);
  const callEndedNoticeSentRef = useRef(false);
  const redirectAfterCallRef = useRef(() => { });
  const lastEndedReasonRef = useRef(null);
  const lastHangupPartyRef = useRef(null);
  const lastActiveCallIdRef = useRef(null);
  /** Sum of tip USD received this Matrix call session (creator only); TipModal amounts are dollars, not platform credits. */
  const callSessionTipUsdRef = useRef(0);
  const lastTipAccumulatorCallIdRef = useRef(null);
  /** Keeps last in-call timer value in sync for call summary + subscriber billing if duration refs read low during hangup. */
  const latestDisplayedCallDurationSecRef = useRef(0);
  const feedsDebounceRef = useRef(null);
  /** Synchronous guard so two placeCall attempts cannot run before `placementBusy` state commits. */
  const placementFlightRef = useRef(false);

  const forceDetachMediaElements = useCallback(() => {
    detachCallMediaElementsFromRefs({
      localVideoRef,
      remoteVideoRef,
      remoteAudioRef,
    });
  }, []);

  const {
    callRateOverrideFromRoom,
    creatorOutgoingPricing,
    creatorInfoForOutgoingEligibility,
  } = useCreatorOutgoingCallPricing({
    isCreator,
    shouldAutoPlaceCall,
    callMediaType,
    callRateOverride,
    matrixRoom,
    currentUser,
  });

  const billingCreatorProfile = useMemo(() => {
    if (!isCreator) return null;
    if (shouldAutoPlaceCall && creatorInfoForOutgoingEligibility) {
      return creatorInfoForOutgoingEligibility;
    }
    return currentUser;
  }, [isCreator, shouldAutoPlaceCall, creatorInfoForOutgoingEligibility, currentUser]);

  const {
    remoteSubscriberCreditsError,
    creatorOutgoingCreditsPending,
    creatorBlockedForSubscriberCredits,
  } = useCreatorOutgoingSubscriberCredits({
    isCreator,
    shouldAutoPlaceCall,
    creatorOutgoingPricing,
    callRateOverride,
    targetSubscriberId,
  });

  const creatorInfoForBilling = useMemo(() => {
    if (isCreator) return creatorInfoForBillingBase;
    return mergeCallRateIntoCreatorInfo({
      creatorInfo: creatorInfoForBillingBase,
      mediaType: callMediaType,
      callRateOffer: callRateOverrideFromRoom,
    });
  }, [creatorInfoForBillingBase, callRateOverrideFromRoom, isCreator, callMediaType]);

  // Note: opponent display name and avatar.
  const { localParticipantName, opponentDisplayName, opponentAvatar } = useOpponentCallProfile({
    matrixRoom,
    destUserInfo,
    activeCall,
    client,
  });

  const updateFeeds = useCallback((call) => {
    if (!call || call.callHasEnded?.()) return;
    try {
      const nextLocalFeeds = call.getLocalFeeds?.() ?? [];
      const nextRemoteFeeds = call.getRemoteFeeds?.() ?? [];
      setLocalFeeds((prev) => (areFeedsEquivalent(nextLocalFeeds, prev) ? prev : nextLocalFeeds));
      setRemoteFeeds((prev) => (areFeedsEquivalent(nextRemoteFeeds, prev) ? prev : nextRemoteFeeds));
    } catch (err) {
      logCallError(err, 'updateFeeds');
    }
  }, []);

  useEffect(() => {
    const feed = getRemoteUsermediaFeed(remoteFeeds);
    const syncRemoteCameraOff = () => {
      setIsRemoteCameraOff(isCallFeedVideoMuted(feed));
    };
    syncRemoteCameraOff();
    if (!feed?.on) return undefined;
    feed.on(CallFeedEvent.MuteStateChanged, syncRemoteCameraOff);
    return () => {
      feed.off?.(CallFeedEvent.MuteStateChanged, syncRemoteCameraOff);
    };
  }, [remoteFeeds]);

  // Derive current streams: prefer call's usermedia (reliable after answer), then feeds.
  const localFromFeeds =
    localFeeds.find(feedHasVideo) ?? localFeeds.find(feedHasTracks);
  const remoteVideoFromFeeds =
    remoteFeeds.find(feedHasVideo) ?? remoteFeeds.find(feedHasTracks);
  const remoteAudioFromFeeds =
    remoteFeeds.find(feedHasAudio) ?? remoteFeeds.find(feedHasTracks);
  const localStream =
    activeCall?.localUsermediaStream ?? localFromFeeds?.stream ?? null;
  const sdkRemoteStream = activeCall?.remoteUsermediaStream ?? null;
  const remoteVideoStream = pickPreferredRemoteStream({
    sdkRemoteStream,
    feedStream: remoteVideoFromFeeds?.stream ?? null,
    kind: 'video',
  });
  const remoteAudioStream = pickPreferredRemoteStream({
    sdkRemoteStream,
    feedStream: remoteAudioFromFeeds?.stream ?? null,
    kind: 'audio',
  });
  const normalizedRemoteAudioStream = getSingleRemoteAudioTrackStream(remoteAudioStream);

  const callEnded = !activeCall || activeCall.callHasEnded?.();

  const {
    iceConnectionState,
    isIceDisconnectedStable,
    isOnline,
    bandwidthMode,
    clearNetworkTimers,
    resetNetworkUiState,
    clearIceUiState,
  } = useCallNetworkResilience({
    activeCall,
    endCall,
    hasConnected: session.hasConnected,
    localStream,
  });

  const clearAuxiliaryCallTimers = useCallback(() => {
    clearNetworkTimers();
    if (feedsDebounceRef.current) {
      clearTimeout(feedsDebounceRef.current);
      feedsDebounceRef.current = null;
    }
  }, [clearNetworkTimers]);

  const { bumpMediaAttachGeneration } = useCallMediaAttachment({
    localVideoRef,
    remoteVideoRef,
    remoteAudioRef,
    localStream,
    remoteVideoStream,
    remoteAudioStream: normalizedRemoteAudioStream,
    callEnded,
  });

  const {
    cameraOff,
    toggleCamera,
    resetCameraState,
  } = useSubscriberCallCameraToggle({
    activeCall,
    localStream,
    enabled: isSubscriber && !isVoiceCallUi,
    onToggleSuccess: () => {
      if (activeCall && !activeCall.callHasEnded?.()) {
        updateFeeds(activeCall);
        bumpMediaAttachGeneration();
      }
    },
  });

  const resetCallMediaState = useCallback(() => {
    setLocalFeeds([]);
    setRemoteFeeds([]);
    setIsRemoteCameraOff(false);
    setMicMuted(false);
    if (isSubscriber) {
      resetCameraState();
    }
  }, [isSubscriber, resetCameraState]);

  const {
    showCameraFlipControl,
    cameraFlipBusy,
    cameraFlipDisabled,
    isCameraFlipped,
    isRemoteCameraFlipped,
    handleFlipCamera,
  } = useCallCameraFlip({
    client,
    isInitialized,
    roomId,
    activeCall,
    localStream,
    isVoiceCallUi,
    isCallConnected,
    onFlipSuccess: () => {
      if (activeCall && !activeCall.callHasEnded?.()) {
        updateFeeds(activeCall);
        // localUsermediaStream often keeps the same object when the camera track is swapped;
        // deps on `localStream` alone won't re-run attachStream (frozen preview). Force rebind.
        bumpMediaAttachGeneration();
      }
    },
  });

  const hasLocalStream = streamHasLiveVideoTrack(localStream);
  const hasRemoteStream = streamHasLiveVideoTrack(remoteVideoStream);
  // Outgoing ringback is owned by CallManager.placeCall — avoid React hook stop races.

  const callTypeLabel = callMediaType;

  const {
    callDurationSec,
    durationAccumulatedSecRef,
    durationStartAtRef,
    pauseDurationTimer,
    resumeDurationTimer,
    resetDurationTimer,
  } = useCallDurationTimer({
    client,
    matrixRoom,
    activeCall,
    callTypeLabel,
    roomId,
    callType,
    callUiType,
  });

  const callDuration = formatCallDuration(callDurationSec);
  /** One pricing profile for live "Total collected", summary, and deduct — includes outgoing rate override. */
  const callPricingDetails = useMemo(() => {
    const callKind = isVoiceCallUi ? 'voice' : 'video';
    if (isCreator) {
      if (billingCreatorProfile) {
        return getCallRequestPricingDetails(billingCreatorProfile, callKind);
      }
      return { ok: false };
    }
    return getCallRequestPricingDetails(creatorInfoForBilling, callKind);
  }, [billingCreatorProfile, creatorInfoForBilling, isCreator, isVoiceCallUi]);
  const minimumCallTimeMinutes = useMemo(() => {
    if (!isCreator) return null;
    const pricing = callPricingDetails;
    if (!pricing?.ok) return null;
    const minutes = Number(pricing.minDurationMins);
    if (!Number.isFinite(minutes) || minutes < 1) return null;
    return Math.round(minutes);
  }, [callPricingDetails, isCreator]);
  const callPricingModel = callPricingDetails?.ok ? callPricingDetails.pricingModel : null;

  /** Same USD/min as subscriber billing for this call (override-aware). */
  const creatorRatePerMinuteUsd = useMemo(() => {
    if (!isCreator) return 0;
    if (callPricingDetails?.ok && Number.isFinite(callPricingDetails.usdPerMinute)) {
      return callPricingDetails.usdPerMinute;
    }
    return getCreatorRatePerMinuteUsd({
      isCreator,
      isVoiceCallUi,
      currentUser: billingCreatorProfile || currentUser,
      callRateOverride,
    });
  }, [isCreator, callPricingDetails, isVoiceCallUi, billingCreatorProfile, currentUser, callRateOverride]);

  const totalTipCollectedAmount = useMemo(
    () => {
      if (!isCreator) return '$0.00';
      const callCollectedUsd = calculateCreatorEarningsUsdPerSecond(
        callDurationSec,
        creatorRatePerMinuteUsd
      );
      const tipCollectedUsd = Number(callSessionTipUsdRef?.current || 0);
      const totalCollectedUsd = callCollectedUsd + (Number.isFinite(tipCollectedUsd) ? tipCollectedUsd : 0);
      return `$${totalCollectedUsd.toFixed(2)}`;
    },
    // callTipLedgerRevision intentionally invalidates when tips update (ref is not in deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tipUsd lives in ref; revision drives recompute
    [isCreator, creatorRatePerMinuteUsd, callDurationSec, callTipLedgerRevision]
  );

  useEffect(() => {
    latestDisplayedCallDurationSecRef.current = callDurationSec;
  }, [callDurationSec]);

  const subscriberWalletCredits = Number(walletBalance?.credits);
  useSubscriberCallCreditDeduction({
    isCreator,
    lastCallEndedReason,
    lastCallHangupParty,
    durationAccumulatedSecRef,
    durationStartAtRef,
    latestDisplayedCallDurationSecRef,
    pauseDurationTimer,
    creatorInfo: creatorInfoForBilling,
    resolvedUserId,
    isVoiceCallUi,
    activeCall,
    roomId,
    subscriberWalletCredits: Number.isFinite(subscriberWalletCredits)
      ? subscriberWalletCredits
      : undefined,
  });

  const tipDisabledForCall =
    !isCreator && (callRoomInfoLoading || !destUserInfo || !resolvedUserId);
  const placeCall = isVoiceCallUi ? placeVoiceCall : placeVideoCall;

  // Tip message sender for in-call tipping.
  const sendTextMessageFromCall = useSendTextMessageFromCall({ client, isInitialized, roomId });

  const handleTipSuccessInCallBase = useHandleTipSuccessInChat({
    sendTextMessage: sendTextMessageFromCall,
    scrollToBottom: undefined,
    openTipModalRef: openTipModalRefForCall,
    forceScrollToBottomRef: forceScrollToBottomRefForCall,
  });

  // Creator: handle tip success when subscriber tips during the call.
  const { handleTipSuccessInCall } = useCallTipEarnings({
    callSessionTipUsdRef,
    client,
    handleTipSuccessInCallBase,
    isCreator,
    isInitialized,
    opponentDisplayName,
    roomId,
    setTipText,
    bumpCallTipLedger,
  });

  useEffect(() => {
    if (!tipText) return undefined;
    const id = window.setTimeout(() => setTipText(''), 5000);
    return () => window.clearTimeout(id);
  }, [tipText]);

  // Creator low-credits alert (voice and video); subscriber purchase modal + auto hangup when out of credits.
  const { showCreditsNotify, dismissCreditsNotify, creatorLowBalanceMinutesRemaining, subscriberLowCreditMinutesRemaining } =
    useLowBalanceCreditCallNotify({
      dispatch,
      walletBalance,
      walletBalanceLoading,
      client,
      isInitialized,
      roomId,
      isCreator,
      activeCall,
      sdkCallState,
      matrixRoom,
      isVoiceCallUi,
      creatorInfo: isCreator ? billingCreatorProfile : creatorInfoForBilling,
      callDurationSec,
      endCall,
      setShowPurchaseCreditsModal,
      setPurchaseCreditsLowCreditMode,
      skipLowBalanceProcess: skipLowBalanceProcessForTesting,
    });

  useEffect(() => {
    hasRedirectedRef.current = false;
    missedCallNoticeSentRef.current = false;
    callEndedNoticeSentRef.current = false;
    lastEndedReasonRef.current = null;
    lastHangupPartyRef.current = null;
    latestDisplayedCallDurationSecRef.current = 0;
    clearLastCallEndState();
    setSdkCallState(activeCall?.state || null);
    dispatchCallSession({ type: 'RESET' });
    placementFlightRef.current = false;
    resetNetworkUiState();
    clearAuxiliaryCallTimers();
    // Intentionally keyed on route identity only — not activeCall snapshots.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on navigation identity, not SDK call state
  }, [roomId, callType, callUiType, clearAuxiliaryCallTimers, dispatchCallSession, resetNetworkUiState, clearLastCallEndState]);

  // Room lifecycle notices (start / end / missed).
  useCallLifecycleNotices({
    client,
    isInitialized,
    roomId,
    otherCallUserId: resolvedUserId,
    shouldAutoPlaceCall,
    lastCallEndedReason,
    activeCall,
    incomingCall,
    hasObservedActiveCall: session.hasObservedActiveCall,
    callTypeLabel,
    hasConnectedRef,
    hadLiveCallRef,
    missedCallNoticeSentRef,
    callEndedNoticeSentRef,
    durationAccumulatedSecRef,
    durationStartAtRef,
    formatDuration: formatCallDuration,
  });

  useEffect(() => {
    if (activeCall) {
      dispatchCallSession({ type: 'OBSERVE_ACTIVE_CALL' });
    }
  }, [activeCall, dispatchCallSession]);

  useEffect(() => {
    const currentCallId = activeCall?.callId || null;
    const previousCallId = lastActiveCallIdRef.current;
    if (previousCallId && currentCallId && previousCallId !== currentCallId) {
      setLocalFeeds([]);
      setRemoteFeeds([]);
      clearIceUiState();
    }
    lastActiveCallIdRef.current = currentCallId;
  }, [activeCall?.callId, clearIceUiState]);

  useEffect(() => {
    const id = activeCall?.callId ?? null;
    if (!id) return;
    // Hangup can churn call ids; never wipe tips after the call has ended or summary is pending.
    if (lastCallEndedReason || session.callJustEnded) return;
    if (lastTipAccumulatorCallIdRef.current !== id) {
      callSessionTipUsdRef.current = 0;
      lastTipAccumulatorCallIdRef.current = id;
    }
  }, [activeCall?.callId, lastCallEndedReason, session.callJustEnded]);

  useCallSummaryRedirect({
    activeCall,
    billingCreatorId: isCreator ? currentUser?._id || currentUser?.id || null : null,
    billingCreatorProfile,
    // Creator must bill/display against creator rates (incl. override), never the subscriber profile.
    postCallBillingCreatorInfo: isCreator ? billingCreatorProfile : creatorInfoForBilling,
    callSessionTipUsdRef,
    callUiType,
    durationAccumulatedSecRef,
    durationStartAtRef,
    hasRedirectedRef,
    isCreator,
    lastEndedReasonRef,
    lastHangupPartyRef,
    latestDisplayedCallDurationSecRef,
    navigate,
    opponentAvatar,
    opponentDisplayName,
    redirectAfterCallRef,
    resolvedUserId,
    roomId,
  });

  useEffect(() => {
    lastEndedReasonRef.current = lastCallEndedReason;
    if (lastCallEndedReason) {
      dispatchCallSession({ type: 'CALL_JUST_ENDED' });
    }
  }, [lastCallEndedReason, dispatchCallSession]);

  useEffect(() => {
    lastHangupPartyRef.current = lastCallHangupParty;
  }, [lastCallHangupParty]);

  useEffect(() => {
    if (!activeCall) {
      // During teardown, `activeCall` can become null before Matrix reports why the
      // call ended. `CallManager.endCall()` clears the session and emits before
      // `onSessionEnd` sets `lastCallEndedReason`, so one render can have
      // `activeCall === null` and `lastCallEndedReason === null`. Wiping duration
      // refs in that gap breaks call summary and subscriber `deductCallCreditAction`
      // (both need frozen duration once `lastCallEndedReason` is set).
      if (session.hasObservedActiveCall && session.callJustEnded) return;
      if (session.hasObservedActiveCall && lastCallEndedReason == null) return;

      resetCallMediaState();
      resetDurationTimer();
    }
  }, [activeCall, lastCallEndedReason, resetDurationTimer, session.callJustEnded, session.hasObservedActiveCall, resetCallMediaState]);

  useEffect(() => {
    if (!activeCall || activeCall.callHasEnded?.()) return;

    updateFeeds(activeCall);
    const onFeeds = () => {
      updateFeeds(activeCall);
      if (feedsDebounceRef.current) clearTimeout(feedsDebounceRef.current);
      feedsDebounceRef.current = setTimeout(() => {
        feedsDebounceRef.current = null;
        updateFeeds(activeCall);
      }, FEEDS_DEBOUNCE_MS);
    };
    const onHangup = () => {
      if (hasConnectedRef.current || hadLiveCallRef.current) {
        dispatchCallSession({ type: 'CALL_JUST_ENDED' });
        // Freeze the counter as soon as we learn the call ended from either side.
        pauseDurationTimer();
        // Do not navigate here: lastCallEndedReason is set asynchronously in
        // CallManager.onSessionEnd. Redirect runs in the effect below once the
        // provider has endedReason (subscriber credit deduct runs in the same tick).
      }
    };
    const onState = (state) => {
      setSdkCallState(state);
      updateFeeds(activeCall);
      if (state === CallState.Connected) {
        dispatchCallSession({ type: 'SESSION_CONNECTED' });
        // Actual duration counting is controlled by the pause/resume effect below.
      }
    };

    activeCall.on(CallEvent.FeedsChanged, onFeeds);
    activeCall.on(CallEvent.Hangup, onHangup);
    activeCall.on(CallEvent.State, onState);

    if (activeCall.state === CallState.Connected) {
      dispatchCallSession({ type: 'SESSION_CONNECTED' });
      // Actual duration counting is controlled by the pause/resume effect below.
    }

    const off = () => {
      try {
        activeCall.off(CallEvent.FeedsChanged, onFeeds);
        activeCall.off(CallEvent.Hangup, onHangup);
        activeCall.off(CallEvent.State, onState);
      } catch (err) {
        logCallError(err, 'Matrix call listener cleanup');
      }
      if (feedsDebounceRef.current) {
        clearTimeout(feedsDebounceRef.current);
        feedsDebounceRef.current = null;
      }
      pauseDurationTimer();
    };
    listenersRef.current = off;

    return () => {
      off();
      listenersRef.current = null;
    };
  }, [activeCall, updateFeeds, pauseDurationTimer, dispatchCallSession, hasConnectedRef, hadLiveCallRef]);

  useEffect(() => {
    if (!localStream) return undefined;
    const tracks = localStream.getTracks?.() || [];
    if (!tracks.length) return undefined;

    const handleTrackEnded = () => {
      updateFeeds(activeCall);
    };
    tracks.forEach((track) => {
      track.addEventListener?.('ended', handleTrackEnded);
    });

    return () => {
      tracks.forEach((track) => {
        track.removeEventListener?.('ended', handleTrackEnded);
      });
    };
  }, [localStream, activeCall, updateFeeds]);

  useEffect(() => {
    if (!activeCall || activeCall.callHasEnded?.()) return;
    // Keep mute sticky on mobile when local tracks are recreated mid-call.
    setLocalOutboundAudioTracksEnabled(activeCall, !micMuted);
  }, [activeCall, localStream, micMuted]);

  useEffect(() => {
    micMutedRef.current = micMuted;
  }, [micMuted]);

  useEffect(() => {
    requestedMicMutedRef.current = micMutedRef.current;
    appliedMicMutedRef.current = micMutedRef.current;
  }, [activeCall?.callId]);

  // Pause/resume duration: Connected + ICE ok + online (ICE/offline handled in useCallNetworkResilience).
  useEffect(() => {
    if (!activeCall) return;
    if (activeCall.callHasEnded?.()) {
      pauseDurationTimer();
      return;
    }

    const shouldPause =
      !isCallConnected || isOnline === false || isIceDisconnectedStable || iceConnectionState === 'failed';

    if (shouldPause) {
      pauseDurationTimer();
      return;
    }

    if (session.hasConnected) {
      resumeDurationTimer();
    }
  }, [
    activeCall,
    isCallConnected,
    isOnline,
    isIceDisconnectedStable,
    iceConnectionState,
    pauseDurationTimer,
    resumeDurationTimer,
    session.hasConnected,
  ]);

  useEffect(() => {
    // Remote/local hangup may clear activeCall before provider propagates endedReason.
    // If user already ended the call, redirect with best-available summary payload.
    if (!activeCall && session.hasObservedActiveCall) {
      const hadLiveSession = hasConnectedRef.current || hadLiveCallRef.current;
      const canRedirect =
        Boolean(lastCallEndedReason) || (session.callJustEnded && hadLiveSession);
      if (!canRedirect) return;

      dispatchCallSession({ type: 'CALL_JUST_ENDED' });
      // Freeze duration immediately so both peers render the same duration
      // in CallSummary, even if teardown raced with our hangup listener.
      pauseDurationTimer();
      redirectAfterCallRef.current();
    }
  }, [
    activeCall,
    lastCallEndedReason,
    pauseDurationTimer,
    session.hasObservedActiveCall,
    session.callJustEnded,
    dispatchCallSession,
    hasConnectedRef,
    hadLiveCallRef,
  ]);

  useEffect(() => () => {
    if (typeof listenersRef.current === 'function') {
      listenersRef.current();
      listenersRef.current = null;
    }
    clearAuxiliaryCallTimers();
  }, [clearAuxiliaryCallTimers]);

  const placeCallSafely = useCallback(async () => {
    if (!roomId || session.placementBusy || placementFlightRef.current) return;
    if (shouldAutoPlaceCall && (!hasRoomsLoaded || matrixLoading || !roomMetadata)) return;
    if (creatorBlockedForSubscriberCredits) return;
    if (session.placeAttempts >= 2) return;
    placementFlightRef.current = true;
    dispatchCallSession({ type: 'PLACEMENT_START' });
    let succeeded = false;
    try {
      if (shouldAutoPlaceCall && isCreator && callRateOverride) {
        const callRateContent = buildCallRateOfferEventContent({
          callRateOverride,
          callMediaType,
          senderUserId: client?.getUserId?.(),
        });
        await sendJuicyhouseCallEvent(client, {
          isInitialized,
          roomId,
          content: callRateContent,
        });
      }
      await Promise.resolve(placeCall(roomId));
      succeeded = true;
    } catch (err) {
      logCallError(err, 'placeCallSafely');
    } finally {
      placementFlightRef.current = false;
      dispatchCallSession({ type: 'PLACEMENT_END', ok: succeeded });
    }
  }, [
    roomId,
    shouldAutoPlaceCall,
    isCreator,
    callRateOverride,
    callMediaType,
    client,
    isInitialized,
    placeCall,
    creatorBlockedForSubscriberCredits,
    session.placementBusy,
    session.placeAttempts,
    dispatchCallSession,
    shouldAutoPlaceCall,
    hasRoomsLoaded,
    matrixLoading,
    roomMetadata,
  ]);

  useEffect(() => {
    if (!shouldAutoPlaceCall) return;
    if (!client || !isInitialized || !roomId) return;
    if (!hasRoomsLoaded || matrixLoading || !roomMetadata) return;
    if (callError) return;
    if (shouldBlockOutgoingPlaceCall()) return;
    // After a hard refresh, Matrix may still have a live call object while
    // provider state is being rehydrated. Avoid placing a duplicate invite.
    if (hasInProgressMatrixCall(client)) return;
    if (activeCall) return;
    if (session.outgoingPlaceSucceeded) return;
    if (session.placementBusy) return;
    if (creatorBlockedForSubscriberCredits) return;
    // After any call this visit (including low-balance hangup), `activeCall` is cleared.
    // Outgoing place may have been skipped while a call was already live—do not auto-dial again
    // until a new /call navigation (session reset).
    if (session.hasObservedActiveCall) return;

    placeCallSafely();
  }, [
    client,
    isInitialized,
    roomId,
    activeCall,
    callError,
    placeCallSafely,
    shouldAutoPlaceCall,
    creatorBlockedForSubscriberCredits,
    session.outgoingPlaceSucceeded,
    session.placementBusy,
    session.hasObservedActiveCall,
    hasRoomsLoaded,
    matrixLoading,
    roomMetadata,
  ]);

  const handleEndCall = useCallback(() => {
    dispatchCallSession({ type: 'CALL_JUST_ENDED' });
    pauseDurationTimer();
    [localStream, remoteVideoStream, remoteAudioStream]
      .filter(Boolean)
      .forEach((stream) => forceStopStreamTracks(stream));
    forceDetachMediaElements();
    resetCallMediaState();
    if (typeof listenersRef.current === 'function') {
      listenersRef.current();
      listenersRef.current = null;
    }
    endCall();
    // Do not navigate here: endCall() emits activeCall=null before the SDK invokes
    // onSessionEnd (lastCallEndedReason). Immediate redirect unmounted this screen
    // before useSubscriberCallCreditDeduction could run. Redirect runs when
    // lastCallEndedReason is set (effect below).
    if (isCreator && (hasConnectedRef.current || hadLiveCallRef.current)) {
      // Creator flow does not depend on subscriber credit deduction; if SDK teardown
      // events race or lag, force summary redirect shortly after explicit End press.
      // Skip for unanswered calls so missed-call notice + notify API can finish first.
      window.setTimeout(() => {
        redirectAfterCallRef.current();
      }, 250);
    }
  }, [
    dispatchCallSession,
    endCall,
    isCreator,
    hasConnectedRef,
    hadLiveCallRef,
    pauseDurationTimer,
    localStream,
    remoteVideoStream,
    remoteAudioStream,
    forceDetachMediaElements,
    resetCallMediaState,
  ]);

  const toggleMic = useCallback(() => {
    const nextRequested = !requestedMicMutedRef.current;
    requestedMicMutedRef.current = nextRequested;
    micMutedRef.current = nextRequested;
    setMicMuted(nextRequested);

    const applyRequestedMute = async () => {
      if (micToggleInFlightRef.current) return;
      micToggleInFlightRef.current = true;
      try {
        while (appliedMicMutedRef.current !== requestedMicMutedRef.current) {
          const targetMuted = requestedMicMutedRef.current;
          await toggleCallMicrophone({
            activeCall,
            micMuted: !targetMuted,
            setMicMuted: (value) => {
              const nextValue =
                typeof value === 'function' ? value(micMutedRef.current) : value;
              micMutedRef.current = nextValue;
              setMicMuted(nextValue);
            },
          });
          appliedMicMutedRef.current = targetMuted;
        }
      } finally {
        micToggleInFlightRef.current = false;
      }
    };

    void applyRequestedMute();
  }, [activeCall]);

  useEffect(() => {
    const audioEl = remoteAudioRef.current;
    if (!audioEl || !normalizedRemoteAudioStream) return;

    audioEl.volume = 1;

    const retry = () => {
      if (audioEl.paused) audioEl.play?.().catch(() => { });
    };
    retry();
    const id = window.setTimeout(retry, 250);
    return () => window.clearTimeout(id);
  }, [
    activeCall?.callId,
    normalizedRemoteAudioStream,
  ]);

  const handleDismissCallError = useCallback(() => {
    clearError();
    clearOutgoingPlaceCallBlock();
    if (resolvedUserId) {
      navigate.toChat(resolvedUserId, {
        state: roomId ? { preferredRoomId: roomId } : undefined,
      });
    } else {
      navigate.toMessages();
    }
  }, [clearError, navigate, resolvedUserId, roomId]);

  const handleOpenMediaSettings = useCallback(() => {
    openBrowserMediaSettings();
  }, []);

  const handleRetryMediaAccess = useCallback(async () => {
    clearError();
    const media = await acquireCallMediaCapture(callMediaType);
    if (!media.ok) {
      reportMediaError?.(media.error);
      return;
    }
    clearOutgoingPlaceCallBlock();
    if (shouldAutoPlaceCall) {
      await placeCallSafely();
      return;
    }
    if (isIncomingCallRoute && incomingCall) {
      await answerCall();
    }
  }, [
    clearError,
    callMediaType,
    reportMediaError,
    shouldAutoPlaceCall,
    placeCallSafely,
    isIncomingCallRoute,
    incomingCall,
    answerCall,
  ]);

  const handleOpenCredits = useCallback(() => {
    setPurchaseCreditsLowCreditMode(false);
    setShowPurchaseCreditsModal(true);
  }, []);

  const handleOpenTip = useCallback(() => {
    if (!tipDisabledForCall) setShowTipModal(true);
  }, [tipDisabledForCall]);

  const openCreditsHandler = isCreator ? undefined : handleOpenCredits;
  const openTipHandler = isCreator ? undefined : handleOpenTip;

  const callRouteResolving =
    shouldAutoPlaceCall &&
    !activeCall &&
    (!roomId ||
      !hasRoomsLoaded ||
      matrixLoading ||
      !roomMetadata ||
      (isCreator &&
        creatorOutgoingPricing?.ok &&
        !callRateOverride &&
        creatorOutgoingCreditsPending));

  if (!client || !isInitialized) {
    return <CallScreenCenteredMessage message="Coming soon…" />;
  }

  if (callRouteResolving) {
    return <CallScreenCenteredMessage message="Connecting call…" />;
  }

  if (callError) {
    return (
      <CallErrorState
        callError={callError}
        onDismiss={handleDismissCallError}
        onOpenSettings={handleOpenMediaSettings}
        onRetryAccess={handleRetryMediaAccess}
      />
    );
  }

  if (shouldAutoPlaceCall && isCreator && creatorBlockedForSubscriberCredits && !activeCall) {
    return (
      <CallScreenCenteredMessage
        variant="card"
        message={`Subscriber needs at least ${formatCreditsValue(
          creatorOutgoingPricing.minCostCredits
        )} credits to receive this call.`}
      />
    );
  }

  if (
    shouldAutoPlaceCall &&
    isCreator &&
    creatorOutgoingPricing?.ok &&
    !creatorOutgoingCreditsPending &&
    remoteSubscriberCreditsError &&
    !activeCall
  ) {
    return <CallScreenCenteredMessage variant="card" message={remoteSubscriberCreditsError} />;
  }

  if (
    isIncomingCallRoute &&
    !activeCall &&
    !callError
  ) {
    return <CallScreenCenteredMessage message="Connecting call…" />;
  }

  const tipAndCreditsModals = isCreator ? null : (
    <CallingScreenSubscriberModals
      showPurchaseCreditsModal={showPurchaseCreditsModal}
      purchaseCreditsLowCreditMode={purchaseCreditsLowCreditMode}
      purchaseCreditsLowCreditMinutesRemaining={subscriberLowCreditMinutesRemaining}
      onClosePurchaseCredits={() => {
        setPurchaseCreditsLowCreditMode(false);
        setShowPurchaseCreditsModal(false);
        dispatch(fetchWalletBalanceAction());
      }}
      destUserInfo={destUserInfo}
      resolvedUserId={resolvedUserId}
      callMediaType={callMediaType}
      openTipModalRefForCall={openTipModalRefForCall}
      showTipModal={showTipModal}
      onCloseTipModal={() => setShowTipModal(false)}
      handleTipSuccessInCall={handleTipSuccessInCall}
      handleOpenCredits={handleOpenCredits}
    />
  );

  if (isVoiceCallUi) {
    return (
      <>
        <VoiceCallUI
          isCreator={isCreator}
          minimumCallTimeMinutes={minimumCallTimeMinutes}
          pricingModel={callPricingModel}
          totalTipCollectedAmount={totalTipCollectedAmount}
          tipText={tipText}
          opponentDisplayName={opponentDisplayName}
          opponentAvatar={opponentAvatar}
          callDuration={callDuration}
          micMuted={micMuted}
          sdkCallState={sdkCallState}
          iceConnectionState={iceConnectionState}
          isOnline={isOnline}
          onToggleMic={toggleMic}
          onEndCall={handleEndCall}
          onOpenCredits={openCreditsHandler}
          onOpenTip={openTipHandler}
          tipDisabled={tipDisabledForCall}
          showCreditsNotify={showCreditsNotify}
          dismissCreditsNotify={dismissCreditsNotify}
          creatorLowBalanceMinutesRemaining={creatorLowBalanceMinutesRemaining}
        />
        <CallRemoteAudio ref={remoteAudioRef} muted={false} />
        {tipAndCreditsModals}
      </>
    );
  }

  return (
    <>
      <VideoCallUI
        isCreator={isCreator}
        totalTipCollectedAmount={totalTipCollectedAmount}
        tipText={tipText}
        localDisplayName={localParticipantName}
        opponentDisplayName={opponentDisplayName}
        opponentAvatar={opponentAvatar}
        callDuration={callDuration}
        hasRemoteStream={hasRemoteStream}
        isRemoteCameraOff={isRemoteCameraOff}
        hasLocalStream={hasLocalStream}
        localVideoRef={localVideoRef}
        remoteVideoRef={remoteVideoRef}
        micMuted={micMuted}
        sdkCallState={sdkCallState}
        iceConnectionState={iceConnectionState}
        isOnline={isOnline}
        bandwidthMode={bandwidthMode}
        onToggleMic={toggleMic}
        showSubscriberCameraToggle={isSubscriber}
        cameraOff={isSubscriber ? cameraOff : false}
        onToggleCamera={isSubscriber ? toggleCamera : undefined}
        onEndCall={handleEndCall}
        onOpenCredits={openCreditsHandler}
        onOpenTip={openTipHandler}
        tipDisabled={tipDisabledForCall}
        showCreditsNotify={showCreditsNotify}
        dismissCreditsNotify={dismissCreditsNotify}
        creatorLowBalanceMinutesRemaining={creatorLowBalanceMinutesRemaining}
        showCameraFlip={showCameraFlipControl && (!isSubscriber || !cameraOff)}
        cameraFlipBusy={cameraFlipBusy}
        cameraFlipDisabled={cameraFlipDisabled}
        onFlipCamera={handleFlipCamera}
        isCameraFlipped={isCameraFlipped}
        isRemoteCameraFlipped={isRemoteCameraFlipped}
      />
      <CallRemoteAudio ref={remoteAudioRef} muted={false} />
      {tipAndCreditsModals}
    </>
  );
};

export default CallingScreen;
